import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, FileText, Mail, MessageCircle, Phone, Plus, Send, UserRound, Users, CalendarClock, PhoneCall, ClipboardList, CircleDot, Copy, Target, X, Pencil, HeartPulse, NotebookPen } from "lucide-react";
import { useFacility } from "../../../context/FacilityContext";
import { Avatar, PriorityStripe, StatusBadge, useOutside } from "../../components/crm/ui";
import {
  KV,
  NO_DATA,
  PriorityBadge,
  priorityLabel,
  SectionCard,
  SourceLabel,
  TASK_STATUSES,
  TaskStatusBadge,
  apiGet,
  apiPost,
  apiPut,
  fmtDateTime,
  fmtDue,
  fmtPhone,
  fullName,
  initialsOf,
  inputCls,
  phone10,
  statusKeyOf,
  statusOptions,
  taskStatusKey,
  taskStatusLabel,
  taskTypeLabel,
  toLead,
  toMs,
} from "../../components/crm/leadsKit";
import { TaskDrawer } from "../../components/crm/TaskDrawer";
import { CompleteTaskDialog } from "../../components/crm/CompleteTaskDialog";
import { LogActivityDrawer, describeLogged } from "../../components/crm/ActivityLog";
import { VIEW_ONLY_TITLE, ViewOnlyNote, useCrmAccess } from "../../components/crm/crmAccess";
import { AccessNotice, fetchApi, reasonOf } from "../../components/marketing/access";
import { SearchSelect } from "../../components/SearchSelect";
import { InlineLoader, PageLoader } from "../../components/PageLoader";
import { DAY, fmtAgo, fmtDate } from "../../lib/format";

/**
 * CRM → Leads → one lead (design: screens/crm/LeadView.tsx).
 *
 * Real data and actions:
 *   lead, activity, tags, form answers ...... GET /api/leads/:id
 *   statuses (pipeline, close outcomes) ..... GET /api/lead-statuses
 *   assignees ............................... GET /api/staff/assignable-users
 *   change status, reopen, close (reason) ... PUT /api/leads/:id (status, lost_reason); the
 *                                             reason shows on the status-change activity (item 147)
 *   ad placement ............................ ad_placement on GET /api/leads/:id (item 146)
 *   assign .................................. PUT /api/leads/:id (assigned_to)
 *   add note ................................ POST /api/leads/:id/activities (note)
 *   log a call / WhatsApp / email / meeting . POST /api/leads/:id/activities (outcome, duration;
 *                                             the WhatsApp and Call buttons offer to log what happened)
 *   convert to patient ...................... POST /api/patients/convert-lead { lead_id } (marks the lead
 *                                             won and moves it to the clinic's won stage itself)
 *   already a patient? ...................... GET /api/patients?search=phone (converted_from_lead_id / contact_id;
 *                                             convert-lead still doesn't set contact_id and the lead has no
 *                                             patient field, so this search stays the signal)
 *   view only ............................... useCrmAccess (staff CRM permission): edit controls disabled
 *   tasks, task status ...................... GET /api/leads/:id/tasks, PUT /api/leads/:id/tasks/:taskId
 *   add / edit task ......................... TaskDrawer
 *   switch location ......................... FacilityContext
 */

const activityKinds = [
  { id: "all", label: "All" }, { id: "notes", label: "Notes" }, { id: "messages", label: "Calls, messages, meetings" }, { id: "tasks", label: "Tasks" }, { id: "status", label: "Status changes" },
];

function kindIcon(kind) {
  if (kind === "note") return FileText;
  if (kind === "whatsapp") return MessageCircle;
  if (kind === "call") return PhoneCall;
  if (kind === "email") return Mail;
  if (kind === "meeting") return CalendarClock;
  if (kind === "patient") return HeartPulse;
  if (kind === "task-created" || kind === "task-status") return ClipboardList;
  if (kind === "status") return CircleDot;
  if (kind === "assigned") return Users;
  if (kind === "form") return Target;
  return UserRound;
}

function isOverdue(ts) { return ts < Date.now(); }

/** lead_activities row → the design's activity. */
function toActivity(a, statuses) {
  const name = (key) => statuses.find((s) => s.key === key)?.name || key;
  const title = a.title || "";
  let kind = "updated";
  let text = title || "Updated";
  let detail = a.description || "";
  if (title === "Lead Created") { kind = "created"; text = "Lead created"; } else if (a.activity_type === "status_change" && title === "Converted to Patient") {
    kind = "patient";
    text = "Converted to patient";
  } else if (a.activity_type === "status_change") {
    kind = "status";
    text = a.old_status ? `Status changed from ${name(a.old_status)} to ${name(a.new_status)}` : `Status changed to ${name(a.new_status)}`;
    // A close writes its reason into this activity's description (item 147).
    if (title === "Status Changed" && detail) detail = `Reason: ${detail}`;
  } else if (a.activity_type === "assignment") kind = "assigned";
  else if (["call", "whatsapp", "email", "sms", "meeting"].includes(a.activity_type)) {
    kind = a.activity_type === "sms" ? "whatsapp" : a.activity_type;
    const logged = describeLogged(a);
    text = logged.text;
    detail = logged.detail;
  }
  else if (/^task/i.test(title)) kind = /scheduled|created/i.test(title) ? "task-created" : "task-status";
  else if (title === "Lead Created") { kind = "created"; text = "Lead created"; }
  else if (a.activity_type === "note") { kind = "note"; text = title || "Added a note"; }
  // Who finished the task: the name saved with it, else the login that wrote this row.
  let completedBy = "";
  if (title === "Task Completed") {
    let meta = a.metadata;
    if (typeof meta === "string") { try { meta = JSON.parse(meta); } catch (e) { meta = null; } }
    completedBy = meta?.completed_by_name || a.created_by_name || "";
  }
  return { id: a.id, ts: toMs(a.created_at) || 0, kind, text, by: a.created_by_name || "Olli", completedBy, detail };
}

/** lead_tasks row → the design's task. */
function toTask(t) {
  const day = t.due_date ? new Date(t.due_date) : null;
  if (day) {
    const [h, m] = String(t.due_time || "00:00").split(":").map(Number);
    day.setHours(h || 0, m || 0, 0, 0);
  }
  return { id: t.id, title: t.title || "Task", type: taskTypeLabel(t.task_type), priority: priorityLabel(t.priority), completionNotes: t.completion_notes || "", status: taskStatusLabel(t.status), due: day ? day.getTime() : Date.now(), assignee: t.assigned_to_name || "Unassigned" };
}

// Keyed by id so another lead never opens showing the previous one's tasks or details.
export default function LeadView() {
  const { id = "" } = useParams();
  return <LeadViewPage key={id} id={id} />;
}

function LeadViewPage({ id }) {
  const fac = useFacility();
  const access = useCrmAccess();
  const canEdit = access.canEdit;
  const [row, setRow] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null); // a refused or failed load (AccessNotice reason)
  const [logOpen, setLogOpen] = useState(false);
  const [logType, setLogType] = useState("Call");
  const [patient, setPatient] = useState(undefined); // the patient made from this lead; undefined until checked
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  const [toast, setToast] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [taskRows, setTaskRows] = useState(null); // null until the tasks first arrive
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeStatus, setCloseStatus] = useState("");
  const [closeReason, setCloseReason] = useState("");
  const [note, setNote] = useState("");
  const [activityFilter, setActivityFilter] = useState("all");
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskEditing, setTaskEditing] = useState(null);
  const [taskTab, setTaskTab] = useState("open");
  const [copied, setCopied] = useState("");
  const [statusMenu, setStatusMenu] = useState("");
  const closeStatusMenu = useCallback(() => setStatusMenu(""), []);
  const statusRef = useOutside(closeStatusMenu, !!statusMenu);

  const loadLead = useCallback(() => fetchApi(`/api/leads/${id}`).then((r) => {
    if (r.ok && r.data?.data) { setRow(r.data.data); setLoadError(null); return; }
    if (r.status === 404) setNotFound(true);
    else setLoadError(reasonOf(r));
  }), [id]);
  const loadTasks = useCallback(() => apiGet(`/api/leads/${id}/tasks`).then((r) => setTaskRows(r?.success ? r.data || [] : [])), [id]);
  useEffect(() => {
    setRow(null);
    setNotFound(false);
    setLoadError(null);
    loadLead();
    loadTasks();
  }, [loadLead, loadTasks]);
  useEffect(() => {
    apiGet("/api/lead-statuses").then((r) => setStatuses(statusOptions(r?.data)));
    apiGet("/api/staff/assignable-users").then((r) => setStaff((r?.data || []).filter((u) => u.user_id && u.name).map((u) => ({ id: u.user_id, name: u.name }))));
  }, []);

  const lead = useMemo(() => (row ? toLead(row, statuses) : null), [row, statuses]);
  // Has this lead (or its contact) already become a patient?
  const leadPhone = row?.phone || "";
  const leadContactId = row?.contact_id || null;
  useEffect(() => {
    if (!row) return undefined;
    let live = true;
    if (!leadPhone) { setPatient(null); return undefined; }
    apiGet("/api/patients", { search: phone10(leadPhone), limit: 20 }).then((r) => {
      if (!live) return;
      const list = r?.success ? r.data || [] : [];
      setPatient(list.find((p) => String(p.converted_from_lead_id) === String(id)) || (leadContactId ? list.find((p) => String(p.contact_id) === String(leadContactId)) : null) || null);
    });
    return () => { live = false; };
  }, [!!row, leadPhone, leadContactId, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const pipeline = statuses.filter((s) => !s.closed).map((s) => s.name);
  const closedNames = statuses.filter((s) => s.closed).map((s) => s.name);
  const selectedFacility = typeof fac.selectedFacilityId === "number" ? fac.selectedFacilityId : null;
  const outsideFacility = lead && selectedFacility && lead.facilityId && Number(lead.facilityId) !== selectedFacility ? fac.facilities.find((f) => Number(f.id) === Number(lead.facilityId)) : undefined;

  const name = lead ? fullName(lead) || "Unknown contact" : "";
  const tasksLoading = taskRows === null;
  const tasks = useMemo(() => (taskRows || []).map(toTask).sort((a, b) => {
    const aDone = a.status === "Done" || a.status === "Cancelled";
    const bDone = b.status === "Done" || b.status === "Cancelled";
    return Number(aDone) - Number(bDone) || a.due - b.due;
  }), [taskRows]);
  const activities = useMemo(() => (row?.activities || []).map((a) => toActivity(a, statuses)).sort((a, b) => b.ts - a.ts).filter((a) => {
    if (activityFilter === "notes") return a.kind === "note";
    if (activityFilter === "messages") return ["whatsapp", "call", "email", "meeting"].includes(a.kind);
    if (activityFilter === "tasks") return a.kind === "task-created" || a.kind === "task-status";
    if (activityFilter === "status") return a.kind === "status";
    return true;
  }), [row, statuses, activityFilter]);
  const openCount = tasks.filter((t) => t.status === "To do" || t.status === "In progress").length;
  const doneCount = tasks.length - openCount;
  const visibleTasks = tasks.filter((t) => taskTab === "open" ? t.status === "To do" || t.status === "In progress" : t.status === "Done" || t.status === "Cancelled");
  // The status dropdown works in keys and shows the same names as the rest of
  // the panel, so it always agrees with the status badge. Closing needs a reason, so closed statuses stay under "Close lead"; a closed
  // lead's own status is listed only so the dropdown can show it.
  const statusChoices = statuses
    .filter((s) => !s.closed || s.key === lead?.statusKey)
    .map((s) => ({ value: s.key, label: s.name, disabled: s.closed }));

  const update = async (body) => {
    try { await apiPut(`/api/leads/${id}`, body); } catch (e) { /* the reload below shows what was saved */ }
    loadLead();
  };
  const changeStatus = (status) => {
    if (!lead || status === lead.status) return;
    const key = statusKeyOf(statuses, status);
    if (key) update({ status: key });
  };
  const changeStatusKey = (key) => {
    if (lead && key && key !== lead.statusKey) update({ status: key });
  };
  const reopenName = statuses.find((s) => s.system === "contacted")?.name || pipeline[1] || pipeline[0];
  const closeLead = () => {
    const outcome = closeStatus || closedNames[0];
    if (!lead || !closeReason.trim() || !outcome) return;
    update({ status: statusKeyOf(statuses, outcome), lost_reason: closeReason.trim() });
    setCloseOpen(false);
    setCloseReason("");
  };
  // Convert to patient (old LeadDetails): creates the patient and moves the lead to the won stage.
  const convert = async () => {
    if (!lead || converting) return;
    setConverting(true);
    setConvertError("");
    try {
      const r = await apiPost("/api/patients/convert-lead", { lead_id: lead.id });
      const made = r?.data || null;
      setPatient(made);
      setConvertOpen(false);
      setToast({ message: `${name} is now a patient${made?.patient_id_number ? ` (${made.patient_id_number})` : ""}`, patientId: made?.id });
    } catch (e) {
      const existing = e?.response?.status === 409 ? e.response.data?.patient_id : null;
      if (existing) { setPatient({ id: existing }); setConvertOpen(false); setToast({ message: "This lead was already converted", patientId: existing }); }
      else setConvertError(e?.response?.data?.message || "Couldn't convert this lead. Try again.");
    }
    setConverting(false);
    loadLead();
  };
  const openLog = (type) => { if (!canEdit) return; setLogType(type); setLogOpen(true); };
  const logActivity = async (body) => {
    await apiPost(`/api/leads/${id}/activities`, body);
    setToast({ message: "Activity logged" });
    loadLead();
  };
  const addNote = async () => {
    if (!lead || !note.trim()) return;
    try {
      await apiPost(`/api/leads/${id}/activities`, { activity_type: "note", title: "Added a note", description: note.trim() });
      setNote("");
    } catch (e) { /* the note stays in the box so nothing is lost */ }
    loadLead();
  };
  const setTaskStatus = async (taskId, status) => {
    setStatusMenu("");
    try { await apiPut(`/api/leads/${id}/tasks/${taskId}`, { status: taskStatusKey(status) }); } catch (e) { /* reload shows the saved state */ }
    loadTasks();
    loadLead();
  };
  // Marking a task done, from the check or the status menu, opens "Complete
  // task?" with an optional note, as on the Tasks page. The PUT keeps the note
  // on the task and writes it into the "Task Completed" activity.
  const [completing, setCompleting] = useState(null);
  const changeTaskStatus = (task, status) => {
    if (status !== "Done") { setTaskStatus(task.id, status); return; }
    setStatusMenu("");
    if (task.status !== "Done") setCompleting(task);
  };
  const confirmComplete = async (task, notes) => {
    setCompleting(null);
    try {
      await apiPut(`/api/leads/${id}/tasks/${task.id}`, { status: taskStatusKey("Done"), ...(notes ? { completion_notes: notes } : {}) });
    } catch (e) {
      // Not silent: the person has just typed a note that wasn't saved.
      setToast({ message: e.response?.data?.message || "Couldn't complete the task. Try again." });
    }
    loadTasks();
    loadLead();
  };
  const copyValue = async (key, value) => {
    try { await navigator.clipboard.writeText(value); setCopied(key); window.setTimeout(() => setCopied(""), 1500); } catch { setCopied(""); }
  };

  if (loadError) return <main className="mx-auto flex max-w-7xl flex-col gap-5 px-8 py-9"><Link to="/crm/leads" className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />Leads</Link><AccessNotice reason={loadError} onRetry={() => { setLoadError(null); loadLead(); }} /></main>;
  if (notFound) return <main className="mx-auto flex max-w-7xl flex-col gap-5 px-8 py-9"><h1 className="text-h1 font-semibold">Lead not found</h1><p className="text-small text-muted">This lead may have been removed or the link may be out of date.</p><Link to="/crm/leads" className="w-fit text-small font-semibold text-ink underline">Back to leads</Link></main>;
  if (!lead) return <main className="mx-auto flex max-w-7xl flex-col gap-6 px-8 py-9"><PageLoader label="Loading lead…" /></main>;

  const utm = Object.entries({ utm_source: lead.utm.source, utm_medium: lead.utm.medium, utm_campaign: lead.utm.campaign, utm_content: lead.utm.content, utm_term: lead.utm.term }).filter((entry) => !!entry[1]);
  const meta = row.ad_id || row.campaign_id || row.adset_id || row.meta_form_id || row.external_lead_id ? row : null;
  let device = "";
  try { const d = typeof row.device_info === "string" ? JSON.parse(row.device_info) : row.device_info; device = d ? [d.device_type || d.device, d.browser, d.os].filter(Boolean).join(" · ") : ""; } catch (e) { device = ""; }
  const web = row.landing_page_url || row.referrer_url || row.gclid || device ? { landingPage: row.landing_page_url || "", referrer: row.referrer_url || "", device, gclid: row.gclid || "" } : null;
  const formAnswers = row.form_responses || [];
  const grouped = activities.reduce((groups, item) => {
    const date = new Date(item.ts);
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    let label = fmtDate(item.ts);
    if (date.toDateString() === new Date().toDateString()) label = "Today";
    else if (date.toDateString() === new Date(Date.now() - DAY).toDateString()) label = "Yesterday";
    const last = groups[groups.length - 1];
    if (last && last.day === dayKey) last.items.push(item);
    else groups.push({ day: dayKey, label, items: [item] });
    return groups;
  }, []);

  return <main className="mx-auto flex max-w-7xl flex-col gap-6 px-8 py-9 animate-fade-in">
    {outsideFacility && <p className="text-small text-muted">This lead belongs to {outsideFacility.name}. <button type="button" onClick={() => fac.selectFacility(outsideFacility.id)} className="font-medium text-sky-deep hover:underline">Switch to {outsideFacility.name}</button></p>}
    <header className="flex flex-col gap-4">
      <Link to="/crm/leads" className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />Leads</Link>
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex min-w-0 items-center gap-4"><Avatar name={name} initials={initialsOf(lead) || "?"} size="lg" /><div className="min-w-0"><h1 className="truncate text-h2 font-semibold">{name}</h1><p className="mt-1 text-small text-muted">{lead.ref}{lead.phone ? ` · ${fmtPhone(lead.phone)}` : ""}{lead.email ? ` · ${lead.email}` : ""}</p></div></div>
        <div className="flex flex-wrap items-center gap-2">
          <SearchSelect className="w-44" value={lead.statusKey} options={statusChoices} onChange={changeStatusKey} placeholder={lead.status} ariaLabel="Lead status" disabled={!canEdit} />
          {/* Book from this lead: the appointment carries lead_id (item 176). */}
          <Link to={`/appointments/new?lead_id=${lead.id}`} className="flex h-11 items-center gap-2 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist"><CalendarClock size={16} strokeWidth={1.75} />Book appointment</Link>
          {lead.contactId && <Link to={`/crm/contacts/${lead.contactId}`} className="flex h-11 items-center rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist">View contact</Link>}
          {/* Opening WhatsApp or the dialler also opens "Log activity", so what happened is kept on the lead. */}
          <a href={`https://wa.me/91${phone10(lead.phone)}`} target="_blank" rel="noreferrer" onClick={() => openLog("WhatsApp")} className="flex h-11 items-center gap-2 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist"><MessageCircle size={16} strokeWidth={1.75} />WhatsApp</a>
          <a href={`tel:${lead.phone}`} onClick={() => openLog("Call")} className="flex h-11 items-center gap-2 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist"><Phone size={16} strokeWidth={1.75} />Call</a>
          {patient ? <Link to={`/patients/${patient.id}`} className="flex h-11 items-center gap-2 rounded-full bg-sky-soft px-4 text-small font-semibold text-ink hover:brightness-95"><HeartPulse size={16} strokeWidth={1.75} />View patient</Link>
            : <button type="button" disabled={!canEdit || patient === undefined} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => { setConvertError(""); setConvertOpen(true); }} className="flex h-11 items-center gap-2 rounded-full bg-ink px-4 text-small font-semibold text-paper hover:bg-ink/85 disabled:cursor-not-allowed disabled:bg-mist disabled:text-muted"><HeartPulse size={16} strokeWidth={1.75} />Convert to patient</button>}
          <div className="relative"><button disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => setCloseOpen((v) => !v)} className="flex h-10 items-center gap-1.5 rounded-full px-3 text-small font-medium text-muted hover:bg-mist hover:text-ink disabled:cursor-not-allowed disabled:opacity-50">Close lead<ChevronDown size={15} strokeWidth={1.75} /></button>{closeOpen && <div className="absolute right-0 top-12 z-20 flex w-72 animate-nav-in flex-col gap-3 rounded-md bg-paper p-4 shadow-md ring-1 ring-line"><label className="flex flex-col gap-1 text-caption text-muted">Outcome<select className={inputCls} value={closeStatus || closedNames[0] || ""} onChange={(e) => setCloseStatus(e.target.value)}>{closedNames.map((s) => <option key={s}>{s}</option>)}</select></label><label className="flex flex-col gap-1 text-caption text-muted">Reason<input className={inputCls} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="Add a short reason" /></label><button disabled={!closeReason.trim() || !closedNames.length} onClick={closeLead} className="h-10 rounded-full bg-sun text-small font-semibold text-ink disabled:bg-mist disabled:text-muted">Close lead</button></div>}</div>
        </div>
      </div>
      {lead.closed && <div className="flex flex-1 items-center justify-between rounded-md bg-mist px-5 py-3"><div className="flex items-center gap-3"><StatusBadge status={lead.status} /><span className="text-small text-muted">This lead is closed.</span></div><button disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => changeStatus(reopenName)} className="h-9 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50">Reopen</button></div>}
      {access.viewOnly && <ViewOnlyNote />}
    </header>

    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
      <div className="flex flex-col gap-6 lg:col-span-8">
        <SectionCard title="Lead details">
          <dl className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
            <KV label="Status"><StatusBadge status={lead.status} /></KV><KV label="Priority"><PriorityBadge priority={lead.priority} /></KV><KV label="Score">{lead.score} / 100</KV>
            <KV label="Interested service">{lead.service}</KV><KV label="Preferred doctor">{lead.doctor || "Any doctor"}</KV><KV label="Facility">{lead.facility}</KV><KV label="Budget">{lead.budget}</KV>
            <div className="flex min-w-0 flex-col gap-1"><label htmlFor="lead-assignee" className="text-caption text-muted">Assigned to</label><SearchSelect id="lead-assignee" size="sm" className="max-w-56" value={lead.assigneeId ? String(lead.assigneeId) : ""} options={[{ value: "", label: "Unassigned" }, ...staff.map((s) => ({ value: String(s.id), label: s.name })), ...(lead.assigneeId && !staff.some((s) => String(s.id) === String(lead.assigneeId)) ? [{ value: String(lead.assigneeId), label: lead.assignee || NO_DATA }] : [])]} onChange={(value) => update({ assigned_to: value ? Number(value) : null })} placeholder="Unassigned" searchable ariaLabel="Assigned to" disabled={!canEdit} /></div><KV label="Next follow-up">{lead.nextFollowUp ? fmtDate(lead.nextFollowUp) : "Not set"}</KV><KV label="Created">{fmtDateTime(lead.createdAt)}</KV><KV label="Last updated">{fmtAgo(lead.updatedAt)}</KV>
            <KV label="Phone">{lead.phone ? fmtPhone(lead.phone) : ""}</KV><KV label="Alternate phone">{lead.altPhone ? fmtPhone(lead.altPhone) : ""}</KV><KV label="Email">{lead.email}</KV><KV label="City">{lead.city}</KV>
            <KV label="Tags"><span className="flex flex-wrap gap-1.5">{lead.tags.length ? lead.tags.map((tag) => <span key={tag} className="rounded-full bg-mist px-2.5 py-1 text-caption font-medium">{tag}</span>) : "Not added"}</span></KV><KV label="Notes">{lead.notes}</KV>
          </dl>
        </SectionCard>

        <SectionCard title="Source">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4"><SourceLabel source={lead.source} /><span className="text-small text-muted">Received {fmtDateTime(lead.createdAt)}</span></div>
          {meta ? <div className="grid grid-cols-1 gap-4 border-b border-line pb-4 sm:grid-cols-2 xl:grid-cols-3"><KV label="Platform">{meta.ad_platform || lead.source}</KV><KV label="Placement">{meta.ad_placement || NO_DATA}</KV><KV label="Campaign">{meta.utm_campaign}</KV><KV label="Campaign ID" mono>{meta.campaign_id}</KV><KV label="Ad set">{meta.adset_name}</KV><KV label="Ad set ID" mono>{meta.adset_id}</KV><KV label="Ad">{meta.ad_name}</KV><KV label="Ad ID" mono>{meta.ad_id}</KV><KV label="Lead form">{meta.meta_form_name}</KV><KV label="Form ID" mono>{meta.meta_form_id}</KV><KV label="Leadgen ID" mono>{meta.external_lead_id}</KV></div> : null}
          {formAnswers.length > 0 && <div className="flex flex-col gap-3 border-b border-line pb-4"><h3 className="text-small font-semibold">Form answers</h3>{formAnswers.map((answer, i) => <div key={`${answer.question}-${i}`} className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"><span className="text-caption text-muted">{answer.question}</span><span className="text-small">{answer.answer}</span></div>)}</div>}
          {utm.length > 0 && <div className="flex flex-col gap-2 border-b border-line pb-4"><h3 className="text-small font-semibold">UTM parameters</h3>{utm.map(([key, value]) => <div key={key} className="grid grid-cols-[130px_minmax(0,1fr)_80px] items-center gap-3 border-b border-line/70 py-2 last:border-0"><span className="font-mono text-caption text-muted">{key}</span><span className="break-all font-mono text-small">{value}</span><button onClick={() => void copyValue(key, value)} className="flex items-center justify-end gap-1 text-caption font-medium text-muted hover:text-ink"><Copy size={13} strokeWidth={1.75} />{copied === key ? "Copied" : "Copy"}</button></div>)}</div>}
          {web && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2"><KV label="Landing page">{web.landingPage}</KV><KV label="Referrer">{web.referrer}</KV><KV label="Device">{web.device}</KV><KV label="GCLID" mono>{web.gclid}</KV></div>}
          {!meta && !web && utm.length === 0 && formAnswers.length === 0 && <p className="text-small text-muted">Added by hand. No tracking data for this lead.</p>}
        </SectionCard>

        <SectionCard title="Activity" action={<button type="button" disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => openLog("Call")} className="flex h-9 items-center gap-1.5 rounded-full px-3 text-small font-semibold ring-1 ring-line hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50"><NotebookPen size={15} strokeWidth={1.75} />Log activity</button>}>
          {canEdit && <div className="flex flex-col gap-3"><textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the team" rows={3} className="w-full resize-y rounded-sm border border-line bg-paper px-4 py-3 text-small outline-none placeholder:text-muted focus:border-ink focus:ring-2 focus:ring-ink/10" /><div className="flex justify-end"><button disabled={!note.trim()} onClick={addNote} className="flex h-10 items-center gap-2 rounded-full bg-sun px-4 text-small font-semibold text-ink disabled:bg-mist disabled:text-muted"><Send size={15} strokeWidth={1.75} />Add note</button></div></div>}
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Activity filters">{activityKinds.map((filter) => <button key={filter.id} role="tab" aria-selected={activityFilter === filter.id} onClick={() => setActivityFilter(filter.id)} className={`h-9 rounded-full px-3.5 text-caption font-medium ${activityFilter === filter.id ? "bg-ink text-paper" : "bg-paper text-muted ring-1 ring-line hover:text-ink"}`}>{filter.label}</button>)}</div>
          {activities.length === 0 ? <p className="py-5 text-small text-muted">No activity in this view yet.</p> : <div className="flex flex-col gap-5">{grouped.map((group, gi) => <section key={group.day} className="animate-fade-in" style={{ animationDelay: `${gi * 35}ms` }}><h3 className="mb-3 text-caption font-semibold text-muted">{group.label}</h3><ol className="relative ml-3 flex flex-col gap-5 border-l border-line pl-6">{group.items.map((item) => { const Icon = kindIcon(item.kind); return <li key={item.id} className="relative flex gap-3"><span className="absolute -left-[31px] top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-mist text-ink"><Icon size={13} strokeWidth={1.75} /></span><div className="min-w-0 flex-1"><div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"><p className="text-small font-medium">{item.text}</p><time className="shrink-0 text-caption text-muted">{fmtDateTime(item.ts)}</time></div><p className="mt-1 text-caption text-muted">{item.by}</p>{item.completedBy && <p className="mt-0.5 text-caption text-muted">Completed by <span className="font-medium text-ink">{item.completedBy}</span></p>}{item.detail && <p className={`mt-2 whitespace-pre-wrap rounded-sm px-3 py-2 text-small ${item.kind === "whatsapp" ? "bg-mist" : "border-l-2 border-line text-muted"}`}>{item.kind === "whatsapp" ? `“${item.detail}”` : item.detail}</p>}</div></li>; })}</ol></section>)}</div>}
        </SectionCard>
      </div>

      <aside className="flex flex-col gap-6 lg:sticky lg:top-6 lg:col-span-4">
        <SectionCard
          title="Tasks"
          action={
            <button type="button" disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => { setTaskEditing(null); setTaskOpen(true); }} className="flex h-9 items-center gap-1.5 rounded-full px-3 text-small font-semibold hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50">
              <Plus size={15} strokeWidth={1.75} />Add task
            </button>
          }
        >
          <p className="-mt-3 text-caption text-muted">{tasksLoading ? <InlineLoader size={14} /> : <>{openCount} open task{openCount === 1 ? "" : "s"}</>}</p>
          <div className="flex gap-1 border-b border-line">
            <button onClick={() => setTaskTab("open")} className={`px-3 py-2 text-small font-medium ${taskTab === "open" ? "border-b-2 border-ink text-ink" : "text-muted"}`}>
              Open <span className="text-caption">{tasksLoading ? "" : openCount}</span>
            </button>
            <button onClick={() => setTaskTab("done")} className={`px-3 py-2 text-small font-medium ${taskTab === "done" ? "border-b-2 border-ink text-ink" : "text-muted"}`}>
              Done <span className="text-caption">{tasksLoading ? "" : doneCount}</span>
            </button>
          </div>
          {tasksLoading ? (
            <PageLoader label="Loading tasks…" className="py-10" />
          ) : visibleTasks.length === 0 ? (
            <p className="py-5 text-small text-muted">{taskTab === "open" ? "No open tasks. Add one to keep the follow-up moving." : "No completed tasks yet."}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {visibleTasks.map((task, i) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  index={i}
                  menuOpen={statusMenu === task.id}
                  onMenu={() => setStatusMenu((v) => (v === task.id ? "" : task.id))}
                  menuRef={statusMenu === task.id ? statusRef : undefined}
                  onStatus={(status) => changeTaskStatus(task, status)}
                  onEdit={() => { setTaskEditing(task.id); setTaskOpen(true); }}
                  readOnly={!canEdit}
                />
              ))}
            </ul>
          )}
        </SectionCard>
      </aside>
    </div>
    <LogActivityDrawer open={logOpen} onClose={() => setLogOpen(false)} onSave={logActivity} target="lead" context={`${name} · ${lead.ref}`} initialType={logType} />
    {convertOpen && <ConvertDialog name={name} lead={lead} wonName={statuses.find((s) => s.won)?.name || "Won"} busy={converting} error={convertError} onCancel={() => setConvertOpen(false)} onConfirm={convert} />}
    {completing && <CompleteTaskDialog task={completing} onCancel={() => setCompleting(null)} onConfirm={(notes) => confirmComplete(completing, notes)} />}
    {toast && <LeadToast message={toast.message} patientId={toast.patientId} onDone={() => setToast(null)} />}
    <TaskDrawer open={taskOpen} onClose={() => setTaskOpen(false)} taskId={taskEditing ?? undefined} leadId={lead.id} onSaved={() => { loadTasks(); loadLead(); }} onDeleted={() => { loadTasks(); loadLead(); }} />
  </main>;
}

/** Convert to patient: what it creates, and that the lead is marked won. */
function ConvertDialog({ name, lead, wonName, busy, error, onCancel, onConfirm }) {
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onCancel]);
  return createPortal(
    <div className="olli fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-ink/35 p-4 font-sans text-ink" onMouseDown={onCancel}>
      <section role="alertdialog" aria-modal="true" aria-labelledby="convert-title" onMouseDown={(e) => e.stopPropagation()} className="flex w-[460px] max-w-full animate-nav-in flex-col gap-5 rounded-xl bg-paper p-7 shadow-lift">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sun-soft text-ink"><HeartPulse size={19} strokeWidth={1.75} /></span><h2 id="convert-title" className="pt-1.5 text-h3 font-semibold">Convert {name} to a patient?</h2></div>
          <button type="button" onClick={onCancel} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><X size={18} strokeWidth={1.75} /></button>
        </div>
        <div className="flex flex-col gap-2 text-small text-muted">
          <p>Olli creates a patient record from this lead (name, phone{lead.email ? ", email" : ""}{lead.doctor ? ", preferred doctor" : ""} and notes) and marks lead {lead.ref} as {wonName}.</p>
          <p>Add the rest of their details on the patient record.</p>
        </div>
        {error && <p role="alert" className="rounded-sm bg-danger-soft px-3 py-2 text-small text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="flex h-11 items-center rounded-full px-5 text-small font-semibold hover:bg-mist">Cancel</button>
          <button type="button" autoFocus disabled={busy} onClick={onConfirm} className="flex h-11 items-center rounded-full bg-sun px-5 text-small font-semibold text-ink hover:brightness-95 disabled:opacity-60">{busy ? "Converting…" : "Convert to patient"}</button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

/** Bottom toast; with a patient, a "View patient" button. */
function LeadToast({ message, patientId, onDone }) {
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => { const t = setTimeout(() => done.current(), 6000); return () => clearTimeout(t); }, [message]);
  return createPortal(
    <div className="olli pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4 font-sans" role="status">
      <div className="pointer-events-auto flex min-h-12 max-w-full animate-nav-in items-center gap-3 rounded-full bg-ink py-1.5 pl-5 pr-2 text-small font-medium text-paper shadow-lift">
        <span className="min-w-0">{message}</span>
        {patientId && <Link to={`/patients/${patientId}`} className="flex h-9 shrink-0 items-center rounded-full bg-paper/10 px-4 font-semibold text-paper hover:bg-paper/20">View patient</Link>}
      </div>
    </div>,
    document.body,
  );
}

function TaskRow({ task, index, menuOpen, onMenu, menuRef, onStatus, onEdit, readOnly }) {
  const done = task.status === "Done" || task.status === "Cancelled";
  return (
    <li className={`relative flex animate-fade-in gap-3 py-4 first:pt-1 ${index === 0 ? "animate-row-flash" : ""}`} style={{ animationDelay: `${Math.min(index, 8) * 25}ms` }}>
      <PriorityStripe priority={task.priority} done={done} />
      <button
        aria-label={done ? "Mark task to do" : "Mark task done"}
        disabled={readOnly}
        title={readOnly ? VIEW_ONLY_TITLE : undefined}
        onClick={() => onStatus(done ? "To do" : "Done")}
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${task.status === "Done" ? "border-success bg-success text-paper" : "border-muted text-transparent hover:border-ink"}`}
      >
        {task.status === "Done" && <Check size={13} strokeWidth={2.5} className="animate-check-draw" />}
        {task.status === "Cancelled" && <X size={12} className="text-muted" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-small font-medium ${task.status === "Done" ? "text-muted line-through" : ""}`}>{task.title}</p>
        <p className={`mt-1 flex items-center gap-1.5 text-caption ${isOverdue(task.due) && !done ? "text-danger" : "text-muted"}`}>
          <CalendarClock size={13} strokeWidth={1.75} />{task.type} · {fmtDue(task.due)}
        </p>
        <p className="mt-1 text-caption text-muted">{task.assignee}</p>
        {task.status === "Done" && task.completionNotes && <p className="mt-1 whitespace-pre-line text-caption text-ink"><span className="font-medium text-muted">Notes: </span>{task.completionNotes}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-3"><TaskStatusBadge status={task.status} /><PriorityBadge priority={task.priority} /></div>
      </div>
      {!readOnly && <><button type="button" aria-label={`Edit ${task.title}`} onClick={onEdit} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink">
        <Pencil size={14} strokeWidth={1.75} />
      </button>
      <div ref={menuRef} className="relative">
        <button aria-label="Change task status" onClick={onMenu} className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink">
          <ChevronDown size={16} strokeWidth={1.75} />
        </button>
        {menuOpen && <div className="absolute right-0 top-9 z-20 flex w-36 animate-nav-in flex-col rounded-sm bg-paper p-1 shadow-md ring-1 ring-line">
          {TASK_STATUSES.map((status) => <button key={status} onClick={() => onStatus(status)} className="rounded-xs px-3 py-2 text-left text-small hover:bg-mist">{status}</button>)}
        </div>}
      </div></>}
    </li>
  );
}
